import { useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X, type LucideIcon } from 'lucide-react';
import { useUIStore } from '../../store/uiStore';

/** Notification type */
type NotificationType = 'success' | 'error' | 'warning' | 'info';

/** Configuration for notification styles (phish severity tones, border-based) */
interface NotificationConfig {
  icon: LucideIcon;
  /** Border color (left accent + outline) */
  borderColor: string;
  iconColor: string;
}

const notificationConfig: Record<NotificationType, NotificationConfig> = {
  success: {
    icon: CheckCircle,
    borderColor: 'border-l-phish-clean',
    iconColor: 'text-phish-clean',
  },
  error: {
    icon: XCircle,
    borderColor: 'border-l-phish-critical',
    iconColor: 'text-phish-critical',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: 'border-l-phish-high',
    iconColor: 'text-phish-high',
  },
  info: {
    icon: Info,
    borderColor: 'border-l-phish-sea',
    iconColor: 'text-phish-sea',
  },
};

interface ToastItemProps {
  id: string;
  type: NotificationType;
  message: string;
  onDismiss: (id: string) => void;
  duration?: number;
}

/**
 * Individual toast notification item
 */
function ToastItem({ id, type, message, onDismiss, duration = 5000 }: ToastItemProps) {
  const config = notificationConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        flex items-start gap-3 p-4 rounded-phish border border-l-2 border-phish-border ${config.borderColor}
        bg-phish-bg-2 shadow-none
        animate-in slide-in-from-right-full duration-300
        max-w-sm w-full
      `}
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${config.iconColor}`} strokeWidth={1.5} />
      <span className="flex-1 text-sm font-normal leading-relaxed text-phish-fg-1">
        {message}
      </span>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 rounded-phish p-1 text-phish-fg-3 transition-colors duration-phish hover:bg-white/5 hover:text-phish-fg-1"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" strokeWidth={1.5} aria-hidden />
      </button>
    </div>
  );
}

interface ToastNotificationProps {
  /** Position of the toast container */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  /** Auto-dismiss duration in milliseconds (0 for no auto-dismiss) */
  duration?: number;
  /** Maximum number of toasts to show at once */
  maxToasts?: number;
}

const positionClasses: Record<NonNullable<ToastNotificationProps['position']>, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

/**
 * ToastNotification component that displays notifications from the UI store
 * Automatically handles notification display and dismissal
 */
export function ToastNotification({
  position = 'top-right',
  duration = 5000,
  maxToasts = 5,
}: ToastNotificationProps) {
  const notifications = useUIStore((state) => state.notifications);
  const removeNotification = useUIStore((state) => state.removeNotification);

  const handleDismiss = useCallback(
    (id: string) => {
      removeNotification(id);
    },
    [removeNotification]
  );

  // Limit displayed notifications
  const displayedNotifications = notifications.slice(-maxToasts);

  if (displayedNotifications.length === 0) {
    return null;
  }

  return (
    <div
      className={`fixed z-50 flex flex-col gap-2 ${positionClasses[position]}`}
      aria-label="Notifications"
    >
      {displayedNotifications.map((notification) => (
        <ToastItem
          key={notification.id}
          id={notification.id}
          type={notification.type}
          message={notification.message}
          onDismiss={handleDismiss}
          duration={duration}
        />
      ))}
    </div>
  );
}

export default ToastNotification;
